import { styled } from "@material-ui/core";
import { useIO, useLinked } from "@type-r/react";
import { AgGridReact } from "ag-grid-react";
import { CollapsableSection, Columns, Flex, FormLabel, FormSelect, Input, Loading, Option } from 'lqx-react';
import React, { useCallback, useRef } from "react";
import NumberFormat from "react-number-format";
import { hasRole } from "src/app/services/can";
import { Company, getCompanies } from "../../api/company";
import { getBuyersAsCommonParticipants, getSuppliersAsCommonParticipants } from "../../api/companyParticipant";
import { Component } from "../../api/component";
import { CreditNote } from "../../api/creditNote";
import { ManualCreditNote } from "../../api/manualCreditNote";
import { NumberWithDecimalEditor, NumberWithoutDecimalEditor, SelectUnitEditor } from "../../components/ag-grid-cell-formating";
import { AgGridReactContainer, StyledAddCircle, StyledRemoveCircle } from "../../components/ag-grid-styles";
import { DigiDateWrapper } from "../../components/date-input";
import { AdditionalFields, AssetColumn, AssetLabel, numberInputStyles } from "../../components/number-input";
import { DigiSelectAsync } from "../../components/select-async";
import { DigiDateInput, ErrorHighligter, asTimelessLinkedDate } from "../../shared";
import { CreditNoteComponentTableColumns } from "./credit-note-component-table-columns";
import { globalStore } from 'api/store';
import { DigiCurrencySelect } from "../../components/select";

export const CreditNoteForm = ({ creditNote }: { creditNote: CreditNote | ManualCreditNote }) => {
    const classes = numberInputStyles();
    const defaultColDef = {
        editable: true,
        filter: false,
        resizable: true,
        minWidth: 150
    };
    const isReady = useIO(
        async () => {
            if (creditNote.dataHolderId) {
                $dataHolder.set((await getCompanies(null, creditNote.dataHolderId)).items[0]);
            }
        });

    const gridApi = useRef(null);
    const onGridReady = (params: { api: any; }) => {
        gridApi.current = params.api;
    };
    const onAddRow = useCallback(() => {
        creditNote.detail.components.unshift(new Component());
        gridApi.current!.setRowData(creditNote.detail.components.models);
        gridApi.current!.setFocusedCell(0, 'number');
        gridApi.current!.startEditingCell({ rowIndex: 0, colKey: 'number' });
    }, [])

    const onRemoveRow = useCallback(() => {
        const selectedData = gridApi.current!.getSelectedRows();
        creditNote.detail.components.remove(selectedData);
        gridApi.current.updateRowData({ remove: selectedData });
    }, []);

    const $dataHolder = useLinked<Company>(new Company())
        .onChange(s => {
            if (s && s.id) {
                creditNote.dataHolderId = Number(s.id);
            }
        });

    return isReady ? <>
        <Columns>
            <AssetColumn>
                <AssetLabel>Supplier *</AssetLabel>
                <ErrorHighligter error={creditNote.errors['supplier']}>
                    <DigiSelectAsync
                        data-qa-id={"supplier"}
                        getItems={getSuppliersAsCommonParticipants}
                        $value={creditNote.$.supplier}
                    />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Buyer *</AssetLabel>
                <ErrorHighligter error={creditNote.errors['buyer']}>
                    <DigiSelectAsync
                        data-qa-id={"buyer"}
                        getItems={getBuyersAsCommonParticipants}
                        $value={creditNote.$.buyer}
                    />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Credit Note Reason</AssetLabel>
                <ErrorHighligter error={creditNote.errors['reason']}>
                    <Input data-qa-id="reason" $value={creditNote.$.reason} aria-label="Credit Note Reason" />
                </ErrorHighligter>
            </AssetColumn>
            {hasRole('Administrator') &&
                <AssetColumn>
                    <AssetLabel>Data Owner</AssetLabel>
                    <DigiSelectAsync
                        data-qa-id={`data owner`}
                        getItems={getCompanies}
                        $value={$dataHolder}
                    />
                </AssetColumn>
            }
            <AssetColumn>
                <AssetLabel>Supplier External ID</AssetLabel>
                <ErrorHighligter error={creditNote.errors['supplierExternalID']}>
                    <Input data-qa-id="supplier external id" $value={creditNote.supplier.$.externalId} aria-label="Supplier External ID" />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Buyer External ID</AssetLabel>
                <ErrorHighligter error={creditNote.errors['buyerExternalID']}>
                    <Input data-qa-id="buyer extrenal id" $value={creditNote.buyer.$.externalId} aria-label="Buyer External ID" />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Amount *</AssetLabel>
                <ErrorHighligter error={creditNote.errors['amount']}>
                    <NumberFormat data-qa-id="amount" value={creditNote.amount} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; creditNote.$.amount.set(floatValue) }} aria-label="Amount" />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Outstanding Amount</AssetLabel>
                <ErrorHighligter error={creditNote.errors['outstandingAmount']}>
                    <NumberFormat data-qa-id="outstanding amount" value={creditNote.outstandingAmount} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; creditNote.$.outstandingAmount.set(floatValue) }} aria-label="Outstanding Amount" />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Currency *</AssetLabel>
                <ErrorHighligter error={creditNote.errors['currency']}>
                    <DigiCurrencySelect
                        aria-label="Currency"
                        isMulti={false}
                        options={globalStore.currenciesLookup.items.models}
                        $value={creditNote.$.currency}
                    />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Issuance Date</AssetLabel>
                <ErrorHighligter error={creditNote.errors['issuanceDate']}>
                    <DigiDateWrapper>
                        <DigiDateInput data-qa-id="issuance date" date $value={asTimelessLinkedDate(creditNote.$.issuanceDate)} aria-label="Insurance Date" />
                    </DigiDateWrapper>
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Credit Note# *</AssetLabel>
                <ErrorHighligter error={creditNote.errors['number']}>
                    <Input data-qa-id="credit note number" $value={creditNote.$.number} aria-label="Credit Note#" />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Payment Terms</AssetLabel>
                <ErrorHighligter error={creditNote.errors['paymentTerms']}>
                    <Input data-qa-id="payment terms" $value={creditNote.$.paymentTerms} aria-label="Payment Terms"/>
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Expiration Date</AssetLabel>
                <ErrorHighligter error={creditNote.errors['expirationDate']}>
                    <DigiDateWrapper>
                        <DigiDateInput data-qa-id="credit note expiration date" date $value={asTimelessLinkedDate(creditNote.$.expirationDate)} />
                    </DigiDateWrapper>
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Sales Order Number</AssetLabel>
                <ErrorHighligter error={creditNote.errors['salesOrderNumber']}>
                    <Input data-qa-id="sales order number" $value={creditNote.detail.saleOrder.$.number} aria-label="Sales Order Number" />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Purchase Order Number</AssetLabel>
                <ErrorHighligter error={creditNote.errors['purchaseOrderNumber']}>
                    <Input data-qa-id="purchase order number" $value={creditNote.detail.purchaseOrder.$.number} aria-label="Purchase Order Number" />
                </ErrorHighligter>
            </AssetColumn>
            <AssetColumn>
                <AssetLabel>Invoice Number</AssetLabel>
                <ErrorHighligter error={creditNote.errors['invoiceNumber']}>
                    <Input data-qa-id="invoice number" $value={creditNote.detail.invoice.$.number} aria-label="Invoice Number" />
                </ErrorHighligter>
            </AssetColumn>
        </Columns>
        <AdditionalFields></AdditionalFields>
        <br />
        <CollapsableSection title="Components" noMargins notExpanded={true}>
            <Section row>
                <AddNewLine row>
                    <StyledAddCircle onClick={onAddRow}></StyledAddCircle>
                    <FormLabel>Add New Line</FormLabel>
                </AddNewLine>
                <RemoveLine row>
                    <StyledRemoveCircle onClick={onRemoveRow}></StyledRemoveCircle>
                    <FormLabel>Remove Line</FormLabel>
                </RemoveLine>
            </Section>
            <AgGridReactContainer className={`ag-theme-balham`}>
                <AgGridReact
                    onGridReady={onGridReady}
                    columnDefs={CreditNoteComponentTableColumns}
                    frameworkComponents={{ NumberWithoutDecimalEditor, NumberWithDecimalEditor, SelectUnitEditor }}
                    rowSelection='multiple'
                    defaultColDef={defaultColDef}
                    overlayNoRowsTemplate={"No Items"}
                    rowData={creditNote.detail.components as any}>
                </AgGridReact>
            </AgGridReactContainer>
        </CollapsableSection>
        <br />
    </> : <Loading />;
};

const Section = styled(Flex)({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: '0 0 5px 0'
})

const AddNewLine = styled(Flex)({
    width: '125px'
})

const RemoveLine = styled(Flex)({
    width: '116px'
})
